import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

/**
 * Surveille le mode "Dispo dans l'heure" du pro connecté :
 * - Notification quand il reste ≤ 10 min (popup toast avec action de renouvellement).
 * - Désactivation automatique côté UI si l'horodatage est dépassé.
 */
export function useInstantRenewal() {
  const { user, role } = useAuth();
  const warnedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || role !== "pro") return;

    let cancelled = false;

    const renew = async () => {
      const until = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from("profiles")
        .update({
          instant_available: true,
          instant_available_until: until,
          instant_last_renewed_at: new Date().toISOString(),
        } as any)
        .eq("user_id", user.id);
      if (!error) {
        toast({
          title: "Mode renouvelé ⚡",
          description: "Vous êtes à nouveau dispo dans l'heure pendant 60 min.",
        });
        warnedRef.current = null;
      }
    };

    const check = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("instant_available, instant_available_until")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      const ia = (data as any).instant_available as boolean | null;
      const until = (data as any).instant_available_until as string | null;
      if (!ia || !until) {
        warnedRef.current = null;
        return;
      }
      const remainingMs = new Date(until).getTime() - Date.now();
      // Expiré → désactiver localement (le cron côté serveur s'en occupe aussi)
      if (remainingMs <= 0) {
        await supabase
          .from("profiles")
          .update({ instant_available: false, instant_available_until: null } as any)
          .eq("user_id", user.id);
        toast({
          title: "Mode expiré ⏰",
          description: "\"Dispo dans l'heure\" désactivé. Réactivez-le si besoin.",
          variant: "destructive",
        });
        warnedRef.current = null;
        return;
      }
      // Avertissement à 10 minutes restantes (une seule fois par cycle)
      if (remainingMs <= 10 * 60 * 1000 && warnedRef.current !== until) {
        warnedRef.current = until;
        toast({
          title: "⚡ Renouveler Dispo dans l'heure ?",
          description: `Plus que ${Math.max(1, Math.round(remainingMs / 60000))} min. Cliquez sur le toast pour prolonger d'1 h.`,
          duration: 10000,
          onClick: () => { void renew(); },
        } as any);
      }
    };

    void check();
    const id = setInterval(check, 60 * 1000); // 1 min
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, role]);
}
